import { logError } from "@/lib/log";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { Target, Pencil, Check, X } from "lucide-react";
import { onVoiceExpenseSaved } from "@/components/VoiceExpense";

function GoalModal({ initial, onSave, onRemove, onClose }) {
  const [value, setValue] = useState(initial ? String(initial) : "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(value);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await onSave(amt);
    } catch (err) {
      logError("Savings goal save failed", err);
      toast.error("Could not save goal — try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-md" data-testid="savings-goal-form">
        <h3 className="font-serif text-3xl mb-2">Monthly savings goal</h3>
        <p className="text-sm text-[#6C7565] mb-6">How much should stay in the ledger each month, after expenses?</p>
        <label className="block mb-8">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Target (₹ per month)</span>
          <input
            type="number" step="1" min="1" required autoFocus
            value={value} onChange={(e) => setValue(e.target.value)}
            data-testid="savings-goal-input"
            className="w-full bg-transparent border-b border-[#1A1F16] py-2 text-3xl font-serif num focus:outline-none focus:border-[#A67C00]"
            placeholder="10000"
          />
        </label>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 border border-[#1A1F16] py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="savings-goal-save-btn" className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628] disabled:opacity-50">{busy ? "Saving…" : "Save goal"}</button>
        </div>
        {initial && (
          <button type="button" onClick={onRemove} data-testid="savings-goal-remove-btn" className="w-full mt-3 text-[10px] uppercase tracking-widest text-[#D94F3D] hover:underline underline-offset-4 py-1">
            Remove goal
          </button>
        )}
      </form>
    </div>
  );
}

function historyBarColor(m) {
  if (m.met) return "bg-[#2E6F40]";
  return m.savings < 0 ? "bg-[#D94F3D]" : "bg-[#D8D4C7]";
}

function progressBarColor(met, saved) {
  if (met) return "bg-[#2E6F40]";
  return saved < 0 ? "bg-[#D94F3D]" : "bg-[#A67C00]";
}

function HistoryStrip({ history, target }) {
  const peak = Math.max(target || 0, ...history.map((m) => Math.max(0, m.savings)), 1);
  return (
    <div className="flex items-end gap-2 h-16" data-testid="savings-goal-history">
      {history.map((m, i) => {
        const h = Math.max(4, Math.round((Math.max(0, m.savings) / peak) * 56));
        return (
          <div key={`${m.month}-${i}`} className="flex-1 flex flex-col items-center gap-1" title={`${m.month}: ${formatINR(m.savings)}`}>
            <div className={`w-full ${historyBarColor(m)}`} style={{ height: `${h}px` }} />
            <span className="font-mono text-[9px] uppercase tracking-wider text-[#6C7565] flex items-center gap-0.5">
              {m.met ? <Check className="w-2.5 h-2.5 text-[#2E6F40]" /> : <X className="w-2.5 h-2.5 text-[#D8D4C7]" />}
              {m.month}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function GoalEmptyState({ onSet }) {
  return (
    <div className="border border-dashed border-[#A67C00] bg-[#FDFBF7] p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="savings-goal-card">
      <div className="flex items-center gap-4">
        <Target className="w-6 h-6 text-[#A67C00]" />
        <div>
          <h3 className="font-serif text-2xl tracking-tight">Set a monthly savings goal.</h3>
          <p className="text-sm text-[#6C7565] mt-0.5">Pick a number to keep every month — the ledger will keep score.</p>
        </div>
      </div>
      <button data-testid="savings-goal-set-btn" onClick={onSet} className="bg-[#A67C00] text-white px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#8a6700] transition-colors">
        Set goal
      </button>
    </div>
  );
}

function GoalProgressCard({ progress, target, onEdit }) {
  const { saved, pct, remaining, month } = progress.current;
  const barPct = Math.max(0, Math.min(100, pct ?? 0));
  const met = saved >= target;
  return (
    <div className="border border-[#D8D4C7] bg-white p-6" data-testid="savings-goal-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565] flex items-center gap-2">
            <Target className="w-3.5 h-3.5 text-[#A67C00]" /> Savings goal · {month}
          </div>
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <span className={`font-serif text-4xl num ${saved < 0 ? "text-[#D94F3D]" : ""}`} data-testid="savings-goal-saved">{formatINR(saved)}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">of {formatINR(target)} target</span>
          </div>
        </div>
        <div className="text-right">
          <div className={`font-serif text-2xl num ${met ? "text-[#2E6F40]" : "text-[#A67C00]"}`} data-testid="savings-goal-pct">{pct}%</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">
            {met ? "Goal met — well kept" : `${formatINR(remaining)} to go`}
          </div>
        </div>
      </div>

      <div className="h-2 bg-[#EAE7DD] mt-4" data-testid="savings-goal-progress-bar">
        <div className={`h-full transition-all ${progressBarColor(met, saved)}`} style={{ width: `${barPct}%` }} />
      </div>

      <div className="mt-5 flex items-end justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <HistoryStrip history={progress.history} target={target} />
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]" data-testid="savings-goal-months-met">
            {progress.months_met}/{progress.history.length} months met
          </span>
          <button data-testid="savings-goal-edit-btn" onClick={onEdit} className="border border-[#1A1F16] px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#1A1F16] hover:text-[#F7F5F0] transition-colors flex items-center gap-1.5">
            <Pencil className="w-3 h-3" /> Edit
          </button>
        </div>
      </div>
    </div>
  );
}

export function SavingsGoalCard() {
  const [progress, setProgress] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/savings-goal/progress");
      setProgress(data);
    } catch (e) {
      logError("Savings goal load failed", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => onVoiceExpenseSaved(() => load()), [load]);

  const save = async (amt) => {
    await api.put("/savings-goal", { monthly_target: amt });
    toast.success("Savings goal set");
    setOpen(false);
    load();
  };
  const remove = async () => {
    await api.delete("/savings-goal");
    toast.success("Goal removed");
    setOpen(false);
    load();
  };

  if (!progress) return null;
  const target = progress.monthly_target;
  return (
    <>
      {target
        ? <GoalProgressCard progress={progress} target={target} onEdit={() => setOpen(true)} />
        : <GoalEmptyState onSet={() => setOpen(true)} />}
      {open && <GoalModal initial={target || null} onSave={save} onRemove={target ? remove : undefined} onClose={() => setOpen(false)} />}
    </>
  );
}
